import { useCallback, useContext, useMemo, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { FeatureFlag } from '@/constants/featureFlags';
import { isIOSUserAgent } from '@/utils/device';
import { GooglePayStripeForm } from './GooglePayStripeForm';
import { GooglePayDirectStripeForm } from './GooglePayDirectStripeForm';
import { GooglePayAdyenForm } from './GooglePayAdyenForm';
import { NuveiGooglePayForm } from './NuveiGooglePayForm';
import { ChargebeeGooglePayForm } from './ChargebeeGooglePayForm';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { TemplateType } from '@/types/boot';
import { useIsWalletZipTaxActive } from '@/hooks/useIsWalletZipTaxActive';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { SubscriptionDisclaimer } from '../Subscriptions/SubscriptionDisclaimer/SubscriptionDisclaimer';

export const GooglePayForm = ({ isDeviceButton }: { isDeviceButton?: boolean }) => {
  const { paymentMethodsSettings, template, addressDetails, customer, checkoutConfiguration, featureFlags } =
    useContext(CheckoutContext);
  const isWalletZipTaxActive = useIsWalletZipTaxActive();
  const [showZipCodeError, setShowZipCodeError] = useState(false);
  const providerSettings = paymentMethodsSettings.find(
    (paymentMethod) => paymentMethod.paymentMethodName === PaymentMethodEnum.GOOGLE_PAY
  )!;

  const shouldShowZipCodeErrorInInput = template.features.showBlockedZipCodeErrorInInput;
  // Google-Pay-as-APM (the tile selection in the APM tab, gated by
  // `shouldShowGooglePayAsApm`) makes Google Pay the primary CTA on ANY
  // template, so it forces the ZIP above the button regardless of the
  // template's own `zipAboveWallet` placement.
  const isGooglePayAsApm = !!checkoutConfiguration?.shouldShowGooglePayAsApm;
  // Subscriptions (Chargebee) select the wallet from a method box, where the
  // button IS the whole form and its click belongs to Chargebee — the ZIP gate
  // is an invisible overlay over it. The field it points the shopper at has to
  // be above that overlay, which is also where `ApplePayForm` keeps it.
  const isChargebee = providerSettings?.provider === ProviderEnum.CHARGEBEE;
  const renderZipAboveWallet = template.type !== TemplateType.DEFAULT || isGooglePayAsApm || isChargebee;
  const zipCode = addressDetails?.zipCode ?? customer.address?.zipCode;
  const handleInvalidTaxClick = useCallback(() => {
    if (shouldShowZipCodeErrorInInput) {
      setShowZipCodeError(true);
    }
  }, [shouldShowZipCodeErrorInInput]);

  // OR_BIBED_15 mitigation: direct pay.js integration instead of Stripe's
  // ECE (whose WebKit relay fails at approval on iOS). See the flag's doc.
  // Scoped to real iOS/iPadOS devices (UA-based) — that is the only place the
  // ECE relay breaks; Android/desktop keep the proven ECE flow even flag-ON.
  const isStripeDirectGooglePay = !!featureFlags[FeatureFlag.STRIPE_DIRECT_GOOGLE_PAY] && isIOSUserAgent();

  const Component = useMemo(() => {
    if (providerSettings?.provider === ProviderEnum.STRIPE) {
      if (isStripeDirectGooglePay) {
        return (
          <GooglePayDirectStripeForm
            onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined}
          />
        );
      }
      return (
        <GooglePayStripeForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
      );
    }
    if (providerSettings?.provider === ProviderEnum.ADYEN) {
      return (
        <GooglePayAdyenForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
      );
    }
    if (providerSettings?.provider === ProviderEnum.NUVEI) {
      // Soft kill switch: missing/undefined keeps wallets on; explicit false hides.
      if (featureFlags[FeatureFlag.NUVEI_WALLETS] === false) {
        return null;
      }
      return (
        <NuveiGooglePayForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
      );
    }
    // Subscriptions: same standalone Chargebee button as before, now selected
    // from a method box instead of sitting in the express row — which is also
    // where its ZIP overlay used to live, so the form now gates itself.
    if (providerSettings?.provider === ProviderEnum.CHARGEBEE) {
      return (
        <ChargebeeGooglePayForm onInvalidTaxClick={shouldShowZipCodeErrorInInput ? handleInvalidTaxClick : undefined} />
      );
    }
    return null;
  }, [providerSettings, handleInvalidTaxClick, shouldShowZipCodeErrorInInput, featureFlags, isStripeDirectGooglePay]);

  if (!Component) {
    return <PaymentMethodNotSupported />;
  }

  // ACDEV-25067 + ACDEV-27914: wallet sheet is the sole input surface for the
  // buyer's postal code — either because `WALLET_ZIP_TAX` is ON (25067) or
  // because the session started without a resolved US/CA tax (27914).
  // Bypass the on-page `InputTaxComponent` + placement logic entirely; the
  // wallet-tax variants under this path (see `{Google,Apple}PayStripeForm`,
  // the Adyen forms' `onClick`, and the direct Stripe form's
  // `onPaymentDataChanged`) handle validation inside the sheet. Nuvei has
  // no in-sheet ZIP path yet, so it keeps the on-page gate.
  if (isWalletZipTaxActive && providerSettings?.provider !== ProviderEnum.NUVEI && !isChargebee) return Component;

  const zipBlock = !isDeviceButton && (
    <InputTaxComponent
      isBlockedForZipCode={showZipCodeError}
      onValidate={(isValid) => {
        if (isValid && zipCode?.trim()) setShowZipCodeError(false);
      }}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {renderZipAboveWallet && zipBlock}
      {/* Subscriptions only (renders null otherwise): ROSCA/FTC renewal
          disclosure directly above the wallet button — see InitialCreditCardForm
          for the card equivalent. */}
      <SubscriptionDisclaimer />
      {Component}
      {!renderZipAboveWallet && zipBlock}
    </div>
  );
};
